import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../auth/AuthProvider";
import { pdf, Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import { useCompany } from "../hooks/useCompany";
import { useUserProfile } from "../hooks/useUserProfile";
import Sidebar from "../components/Sidebar";

const API_URL = process.env.REACT_APP_API_URL;

const UNASSIGNED = "Unassigned";

// ── Helpers ───────────────────────────────────────────────────────────────────
const todayStr = () => new Date().toISOString().split("T")[0];
const jan1Str = () => `${new Date().getFullYear()}-01-01`;

const fmtAmount = (n) =>
  `$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (dateStr) => {
  const [y, m, d] = dateStr.split("-");
  return `${parseInt(m, 10)}/${parseInt(d, 10)}/${y.slice(2)}`;
};

// ── PDF styles (matches the Fausto document: serif, 1-inch margins) ───────────
const s = StyleSheet.create({
  page: {
    fontFamily: "Times-Roman",
    fontSize: 11,
    paddingTop: 72,
    paddingBottom: 60,
    paddingHorizontal: 72,
    color: "#000",
  },
  // Cover
  coverWrap: { flex: 1, justifyContent: "center", alignItems: "center" },
  coverLine: { fontSize: 16, marginBottom: 10, textAlign: "center" },
  // Summary
  summaryTitle: {
    fontFamily: "Times-Bold",
    fontSize: 18,
    textAlign: "center",
    marginBottom: 24,
  },
  summaryRow: { fontSize: 12, marginBottom: 8 },
  summaryTotalRow: { fontSize: 12, marginTop: 16, marginBottom: 8 },
  summaryDepositRow: { fontSize: 12, marginBottom: 6 },
  // Category pages
  catTitle: {
    fontFamily: "Times-Bold",
    fontSize: 26,
    textAlign: "center",
    marginBottom: 14,
  },
  tableRow: { flexDirection: "row", marginBottom: 3 },
  colDate: { width: "11%", fontSize: 10 },
  colDesc: { flex: 1, fontSize: 10 },
  colAmt: { width: "15%", fontSize: 10, textAlign: "right" },
  catTotal: { fontFamily: "Times-Bold", fontSize: 12, marginTop: 10 },
  // Shared
  pageNum: { position: "absolute", bottom: 36, right: 72, fontSize: 10 },
});

// ── PDF Document component ────────────────────────────────────────────────────
const AccountabilityPDF = ({ cover, selectedCats, groupedTxns, depositTxns }) => {
  const summaryRows = selectedCats.map((cat) => {
    const total = (groupedTxns[cat] || []).reduce((sum, t) => sum + t.amount, 0);
    return { cat, total };
  });
  const grandTotal = summaryRows.reduce((sum, r) => sum + r.total, 0);
  const depositTotal = depositTxns.reduce((sum, t) => sum + Math.abs(t.amount), 0);

  return (
    <Document>
      {/* Page 1 — Cover */}
      <Page size="LETTER" style={s.page}>
        <View style={s.coverWrap}>
          <Text style={s.coverLine}>{cover.line1}</Text>
          <Text style={s.coverLine}>{cover.line2}</Text>
          <Text style={s.coverLine}>{cover.line3}</Text>
        </View>
        <Text style={s.pageNum} render={({ pageNumber }) => `${pageNumber}`} fixed />
      </Page>

      {/* Page 2 — Expenses Summary */}
      <Page size="LETTER" style={s.page}>
        <Text style={s.summaryTitle}>Expenses Summary</Text>
        {summaryRows.map((r) => (
          <Text key={r.cat} style={s.summaryRow}>
            {r.cat} Expenses - {fmtAmount(r.total)}
          </Text>
        ))}
        <Text style={s.summaryTotalRow}>
          Total Business Expenses - {fmtAmount(grandTotal)}
        </Text>
        {depositTotal > 0 && (
          <Text style={[s.summaryDepositRow, { marginTop: 12 }]}>
            **Deposits - {fmtAmount(depositTotal)}
          </Text>
        )}
        <Text style={s.pageNum} render={({ pageNumber }) => `${pageNumber}`} fixed />
      </Page>

      {/* Pages 3+ — One section per expense category */}
      {selectedCats.map((cat) => {
        const txns = groupedTxns[cat] || [];
        const total = txns.reduce((sum, t) => sum + t.amount, 0);
        return (
          <Page key={cat} size="LETTER" style={s.page}>
            <Text style={s.catTitle}>{cat}</Text>
            {txns.map((t, i) => (
              <View key={i} style={s.tableRow}>
                <Text style={s.colDate}>{fmtDate(t.date)}</Text>
                <Text style={s.colDesc}>{t.merchant_name || t.name || ""}</Text>
                <Text style={s.colAmt}>{fmtAmount(t.amount)}</Text>
              </View>
            ))}
            <Text style={s.catTotal}>{"        "}Total: {fmtAmount(total)}</Text>
            <Text style={s.pageNum} render={({ pageNumber }) => `${pageNumber}`} fixed />
          </Page>
        );
      })}

      {/* Deposits page */}
      {depositTxns.length > 0 && (
        <Page size="LETTER" style={s.page}>
          <Text style={s.catTitle}>Deposits</Text>
          {depositTxns.map((t, i) => (
            <View key={i} style={s.tableRow}>
              <Text style={s.colDate}>{fmtDate(t.date)}</Text>
              <Text style={s.colDesc}>{t.merchant_name || t.name || ""}</Text>
              <Text style={s.colAmt}>{fmtAmount(Math.abs(t.amount))}</Text>
            </View>
          ))}
          <Text style={s.catTotal}>
            {"        "}Total: {fmtAmount(depositTxns.reduce((sum, t) => sum + Math.abs(t.amount), 0))}
          </Text>
          <Text style={s.pageNum} render={({ pageNumber }) => `${pageNumber}`} fixed />
        </Page>
      )}
    </Document>
  );
};

// ── Main page component ───────────────────────────────────────────────────────
const GenerateAccountabilityStatement = () => {
  const auth = useAuth();
  const token = auth.user?.access_token;
  const { activeCompany } = useCompany();
  const { profile } = useUserProfile();

  const authHeaders = useMemo(
    () => ({ "Content-Type": "application/json", Authorization: `Bearer ${token}` }),
    [token]
  );

  // ── Data ─────────────────────────────────────────────────────────────────────
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]); // [{ account_id, name, last_four, institution }]
  const [userAssignments, setUserAssignments] = useState({});
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notLinked, setNotLinked] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState(new Set()); // account_ids

  // ── Form state ───────────────────────────────────────────────────────────────
  const currentYear = new Date().getFullYear();
  const [startDate, setStartDate] = useState(jan1Str);
  const [endDate, setEndDate] = useState(todayStr);
  const [cover, setCover] = useState({ line1: "", line2: "", line3: "" });
  const [selectedCats, setSelectedCats] = useState(new Set());
  const [includeDeposits, setIncludeDeposits] = useState(true);
  const [generating, setGenerating] = useState(false);

  // Populate cover defaults once profile + company are available
  useEffect(() => {
    const companyName = activeCompany?.name || "";
    const fullName = [profile?.first_name, profile?.last_name].filter(Boolean).join(" ");
    setCover((prev) => ({
      line1: prev.line1 || companyName,
      line2: prev.line2 || (fullName ? `${fullName} Sole Prop.` : ""),
      line3: prev.line3 || `Accountability Statement for ${currentYear}`,
    }));
  }, [activeCompany, profile, currentYear]);

  // ── Fetch ─────────────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const statusRes = await fetch(`${API_URL}/api/bank/status`, { headers: authHeaders });
      if (statusRes.ok) {
        const s = await statusRes.json();
        if (!s.linked) { setNotLinked(true); setLoading(false); return; }
      }

      const [txRes, balRes, assignRes, catRes] = await Promise.all([
        fetch(`${API_URL}/api/bank/transactions?days=365`, { headers: authHeaders }),
        fetch(`${API_URL}/api/bank/balance`, { headers: authHeaders }),
        fetch(`${API_URL}/api/bank/transactions/categories`, { headers: authHeaders }),
        fetch(`${API_URL}/api/bank/categories`, { headers: authHeaders }),
      ]);

      if (txRes.ok) {
        const d = await txRes.json();
        setTransactions(d.transactions || []);
      }
      if (balRes.ok) {
        const d = await balRes.json();
        const accts = d.accounts || [];
        setAccounts(accts);
        setSelectedAccounts(new Set(accts.map((a) => a.account_id)));
      }
      if (assignRes.ok) setUserAssignments(await assignRes.json());
      if (catRes.ok) {
        const cats = await catRes.json();
        setAllCategories(cats);
        // Default: all non-Deposits/Unassigned categories selected
        setSelectedCats(
          new Set(
            cats
              .map((c) => c.name)
              .filter((n) => n !== UNASSIGNED && n.toLowerCase() !== "deposits")
          )
        );
      }
    } catch (err) {
      console.error("Failed to load:", err);
    } finally {
      setLoading(false);
    }
  }, [token, authHeaders]);

  useEffect(() => {
    if (auth.isAuthenticated) fetchAll();
  }, [auth.isAuthenticated, fetchAll]);

  // ── Derived data ──────────────────────────────────────────────────────────────
  const effectiveCat = useCallback(
    (txn) => userAssignments[txn.transaction_id]?.category_name || UNASSIGNED,
    [userAssignments]
  );

  const { groupedExpenses, depositTxns, catStats } = useMemo(() => {
    const grouped = {};
    const deposits = [];

    transactions
      .filter(
        (t) =>
          t.date >= startDate &&
          t.date <= endDate &&
          (selectedAccounts.size === 0 || selectedAccounts.has(t.account_id))
      )
      .forEach((t) => {
        if (t.amount < 0) {
          deposits.push(t);
          return;
        }
        const cat = effectiveCat(t);
        if (cat === UNASSIGNED) return;
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat].push(t);
      });

    const stats = {};
    Object.entries(grouped).forEach(([cat, txns]) => {
      stats[cat] = {
        count: txns.length,
        total: txns.reduce((sum, t) => sum + t.amount, 0),
      };
    });

    return { groupedExpenses: grouped, depositTxns: deposits, catStats: stats };
  }, [transactions, startDate, endDate, selectedAccounts, effectiveCat]);

  // All selectable categories: union of categories with transactions + all from DB
  const availableCats = useMemo(() => {
    const fromTxns = Object.keys(groupedExpenses);
    const fromDB = allCategories
      .map((c) => c.name)
      .filter((n) => n !== UNASSIGNED && n.toLowerCase() !== "deposits");
    return Array.from(new Set([...fromDB, ...fromTxns])).sort();
  }, [groupedExpenses, allCategories]);

  const grandExpenseTotal = useMemo(
    () => Array.from(selectedCats).reduce((sum, cat) => sum + (catStats[cat]?.total || 0), 0),
    [selectedCats, catStats]
  );
  const depositTotal = useMemo(
    () => depositTxns.reduce((sum, t) => sum + Math.abs(t.amount), 0),
    [depositTxns]
  );

  // Ordered list of selected cats that actually have transactions
  const activeCats = useMemo(
    () => availableCats.filter((c) => selectedCats.has(c) && catStats[c]),
    [availableCats, selectedCats, catStats]
  );

  // ── Generate PDF ──────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const blob = await pdf(
        <AccountabilityPDF
          cover={cover}
          selectedCats={activeCats}
          groupedTxns={groupedExpenses}
          depositTxns={includeDeposits ? depositTxns : []}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `accountability_statement_${startDate}_${endDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF generation failed:", err);
    } finally {
      setGenerating(false);
    }
  };

  const handleSignOut = () => {
    auth.signOut();
  };

  // ── Estimated page count for display ─────────────────────────────────────────
  const estimatedPages =
    2 + activeCats.length + (includeDeposits && depositTxns.length > 0 ? 1 : 0);
  const totalTxnCount =
    activeCats.reduce((sum, c) => sum + (catStats[c]?.count || 0), 0) +
    (includeDeposits ? depositTxns.length : 0);

  // ── Render ────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Sidebar handleSignOut={handleSignOut}>
        <div className="flex flex-1 items-center justify-center min-h-screen">
          <div className="flex flex-col items-center gap-3">
            <svg className="animate-spin h-12 w-12 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <p className="text-gray-400 text-sm">Loading your transactions…</p>
          </div>
        </div>
      </Sidebar>
    );
  }

  if (notLinked) {
    return (
      <Sidebar handleSignOut={handleSignOut}>
        <div className="p-6 text-gray-500 text-sm">
          No linked bank account found. Please connect a bank account first.
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar handleSignOut={handleSignOut}>
      <div className="p-4 sm:p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">
            Generate Accountability Statement
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Select a date range and categories to include, customize your cover page, then generate a formatted PDF.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ── Left: configuration ────────────────────────────────────────── */}
          <div className="flex flex-col gap-5">
            {/* Date range */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h2 className="text-sm font-semibold text-gray-700 mb-3">Date Range</h2>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">From</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">To</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
                  />
                </div>
              </div>
            </div>

            {/* Bank account selector */}
            {accounts.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold text-gray-700">Bank Accounts</h2>
                  <div className="flex gap-2 text-xs">
                    <button
                      onClick={() => setSelectedAccounts(new Set(accounts.map((a) => a.account_id)))}
                      className="text-blue-600 hover:underline"
                    >
                      All
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={() => setSelectedAccounts(new Set())}
                      className="text-blue-600 hover:underline"
                    >
                      None
                    </button>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  {accounts.map((acct) => {
                    const checked = selectedAccounts.has(acct.account_id);
                    const label = acct.name || acct.account_id;
                    const sub = [
                      acct.institution?.name,
                      acct.last_four ? `····${acct.last_four}` : null,
                    ]
                      .filter(Boolean)
                      .join(" · ");
                    return (
                      <label key={acct.account_id} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            setSelectedAccounts((prev) => {
                              const next = new Set(prev);
                              next.has(acct.account_id)
                                ? next.delete(acct.account_id)
                                : next.add(acct.account_id);
                              return next;
                            })
                          }
                          className="w-4 h-4 accent-green-500 shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-700 truncate">{label}</p>
                          {sub && <p className="text-xs text-gray-400 truncate">{sub}</p>}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Cover page editor */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h2 className="text-sm font-semibold text-gray-700 mb-3">Cover Page</h2>
              <div className="flex flex-col gap-3">
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Company Name</label>
                  <input
                    type="text"
                    value={cover.line1}
                    onChange={(e) => setCover((p) => ({ ...p, line1: e.target.value }))}
                    placeholder="e.g. DBA Obed's Painting"
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Full Name / Entity</label>
                  <input
                    type="text"
                    value={cover.line2}
                    onChange={(e) => setCover((p) => ({ ...p, line2: e.target.value }))}
                    placeholder="e.g. John Doe Sole Prop."
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 block mb-1">Title</label>
                  <input
                    type="text"
                    value={cover.line3}
                    onChange={(e) => setCover((p) => ({ ...p, line3: e.target.value }))}
                    placeholder={`Accountability Statement for ${currentYear}`}
                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
                  />
                </div>
              </div>
            </div>

            {/* Category selection */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-700">Expense Categories</h2>
                <div className="flex gap-2 text-xs">
                  <button
                    onClick={() => setSelectedCats(new Set(availableCats))}
                    className="text-blue-600 hover:underline"
                  >
                    All
                  </button>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={() => setSelectedCats(new Set())}
                    className="text-blue-600 hover:underline"
                  >
                    None
                  </button>
                </div>
              </div>

              {availableCats.length === 0 ? (
                <p className="text-sm text-gray-400">
                  No categorized transactions found. Categorize transactions first.
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {availableCats.map((cat) => {
                    const stats = catStats[cat];
                    const checked = selectedCats.has(cat);
                    return (
                      <label key={cat} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            setSelectedCats((prev) => {
                              const next = new Set(prev);
                              next.has(cat) ? next.delete(cat) : next.add(cat);
                              return next;
                            })
                          }
                          className="w-4 h-4 accent-green-500 shrink-0"
                        />
                        <span className="text-sm text-gray-700 flex-1">{cat}</span>
                        <span className="text-xs text-gray-400 whitespace-nowrap">
                          {stats
                            ? `${stats.count} txn${stats.count !== 1 ? "s" : ""} · ${fmtAmount(stats.total)}`
                            : "0 txns"}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}

              {/* Deposits toggle */}
              <div className="mt-4 pt-3 border-t border-gray-100">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeDeposits}
                    onChange={(e) => setIncludeDeposits(e.target.checked)}
                    className="w-4 h-4 accent-green-500 shrink-0"
                  />
                  <span className="text-sm text-gray-700 flex-1">Include Deposits</span>
                  <span className="text-xs text-gray-400 whitespace-nowrap">
                    {depositTxns.length} txn{depositTxns.length !== 1 ? "s" : ""} · {fmtAmount(depositTotal)}
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* ── Right: live preview + generate ────────────────────────────── */}
          <div className="flex flex-col gap-5">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h2 className="text-sm font-semibold text-gray-700 mb-3">Preview</h2>

              {/* Cover preview */}
              <div className="bg-gray-50 rounded-lg p-5 text-center mb-4 border border-gray-100">
                <p className="text-sm font-medium text-gray-800">
                  {cover.line1 || <span className="text-gray-300">Company Name</span>}
                </p>
                <p className="text-sm text-gray-600 mt-1.5">
                  {cover.line2 || <span className="text-gray-300">Full Name</span>}
                </p>
                <p className="text-sm text-gray-600 mt-1.5">
                  {cover.line3 || <span className="text-gray-300">Title</span>}
                </p>
              </div>

              {/* Expenses summary preview */}
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide text-center mb-3">
                Expenses Summary
              </p>
              {activeCats.length === 0 ? (
                <p className="text-xs text-gray-400 text-center mb-3">No categories selected</p>
              ) : (
                <div className="flex flex-col gap-1.5 mb-3">
                  {activeCats.map((cat) => (
                    <div key={cat} className="flex justify-between text-sm">
                      <span className="text-gray-600">{cat} Expenses</span>
                      <span className="text-gray-800 font-medium tabular-nums">
                        {fmtAmount(catStats[cat].total)}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <div className="border-t border-gray-100 pt-2.5 flex justify-between text-sm font-semibold">
                <span>Total Business Expenses</span>
                <span className="tabular-nums">{fmtAmount(grandExpenseTotal)}</span>
              </div>

              {includeDeposits && depositTxns.length > 0 && (
                <div className="flex justify-between text-sm mt-2 text-green-700">
                  <span>**Deposits</span>
                  <span className="tabular-nums">{fmtAmount(depositTotal)}</span>
                </div>
              )}
            </div>

            {/* Generate button */}
            <button
              onClick={handleGenerate}
              disabled={generating || (activeCats.length === 0 && !(includeDeposits && depositTxns.length > 0))}
              className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-white font-medium py-3 rounded-xl transition text-sm"
            >
              {generating ? "Generating PDF…" : "↓ Generate PDF"}
            </button>

            {(activeCats.length > 0 || (includeDeposits && depositTxns.length > 0)) && (
              <p className="text-xs text-center text-gray-400">
                ~{estimatedPages} pages · {totalTxnCount} transactions
              </p>
            )}
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default GenerateAccountabilityStatement;
